/**
 * CameraGeoMapPage (shared) — cameras on a geographic map, beside the live alert panel.
 *
 * The third presentation of the alert board. The honeycomb answers "which camera", the GIS
 * board answers "which door"; this one answers "which building, which street" — the read an
 * operator needs the moment an estate stops being one premises.
 *
 * Everything except the canvas is the board's existing machinery: the same `useAlertBoard`
 * engine, the same `MapMarker`, preview card and hover popover the GIS board draws, and the
 * same docked `AlertTileStack`. That is deliberate — a second spatial board that looked or
 * behaved differently would be a second product to learn.
 *
 * WHERE THE PINS LIVE. A geographic position is a property of the CAMERA (columns on the
 * camera row), not of a layout, because there is only one Earth to place it on. So this
 * board has no layout bar, no per-layout scoping, and no one-camera-one-layout rule — every
 * camera that has coordinates is on it. A camera can carry both a plan pin and coordinates.
 *
 * THE BASEMAP IS NOT GUARANTEED. No tiles ship with the product, so a node without internet
 * has no basemap. `GeoTileMap` detects that and says so; the pins and the alert panel keep
 * working on a plain background, because "which camera is alerting" is still the question
 * the board exists to answer.
 *
 * Node-agnostic. `api` (GeoMapApi) owns coordinates, `alertsApi` (HexAlertsApi) owns
 * realtime, and the per-camera player is injected — local SFUPlayer, cloud HexLivePlayer.
 *
 * Not routed to directly: AlertBoardPage is what the apps mount.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowsPointingOutIcon,
  CheckIcon,
  GlobeAltIcon,
  MapPinIcon,
  MinusIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ClickPreviewCard from '../components/hexagon/ClickPreviewCard';
import BoardLegend from '../components/hexagon/BoardLegend';
import HexAlertPopover from '../components/hexagon/HexAlertPopover';
import AlertTileStack from '../components/hexagon/AlertTileStack';
import Tooltip from '../components/common/Tooltip';
import MapSearchBox from '../components/map/MapSearchBox';
import HexFilterBar, { HexFilterColumn } from '../components/hexagon/HexFilterBar';
import { useAlertTypeFilter } from '../hooks/useAlertTypeFilter';
import ToolbarIdentity from '../components/common/ToolbarIdentity';
import MapMarker, { MARKER_SIZE } from '../components/map/MapMarker';
import MapCluster from '../components/map/MapCluster';
import { clusterPins, groupSpanMetres, type PinPoint } from '../components/map/clusterPins';
import type { HexColorState } from '../components/hexagon/types';
import CameraPalette from '../components/map/CameraPalette';
import GeoTileMap, { type GeoTileMapHandle, type GeoView } from '../components/map/GeoTileMap';
import BasemapSwitcher from '../components/map/BasemapSwitcher';
import { useBasemap } from '../components/map/basemaps';
import { bearingLabel } from '../components/map/PlacementDetailModal';
import {
  viewAlertTarget,
  type HexPlayerComponent,
  type ViewAlertRequest,
  type ViewAlertTarget,
} from '../components/hexagon/types';
import { useAlertBoard } from '../hooks/useAlertBoard';
import type {
  GeoMapApi,
  GeoMapConfig,
  GeoPlacement,
  HexAlert,
  HexAlertsApi,
  HexCamera,
} from '../api/contracts';

/** Where THIS BROWSER was last left, so a reload resumes the operator's position rather than
 *  snapping back to the deployment default they had deliberately navigated away from. */
const STORAGE_KEY = 'trinetra.geomap.view';

/** Fallback cone spread for a camera nobody has surveyed. Matches the GIS board's default. */
const DEFAULT_FOV = 90;

/**
 * Pins closer than this on screen are drawn as one group. A little more than a pin plus its
 * facing notch, so neighbouring pins never touch.
 */
const CLUSTER_RADIUS = 46;
/**
 * Classic zoom from which a group that still overlaps is treated as ONE SPOT — cameras on the
 * same pole or building — and fanned out around it on click instead of zoomed into: zooming
 * cannot separate cameras that share a coordinate.
 */
const SPREAD_ZOOM = 17;
/** A group whose cameras all sit within this many metres is one spot, whatever the zoom. */
const SPREAD_SPAN_M = 40;

export interface CameraGeoMapPageProps {
  api: GeoMapApi;
  alertsApi: HexAlertsApi;
  PlayerComponent: HexPlayerComponent;
  /** Caller holds `map.edit` — shows placement editing and the "save default view" action. */
  canEdit?: boolean;
  /** Cloud: require a selected node; show an empty state when nodeKey is falsy. */
  requireNode?: boolean;
  /** Cloud: changes when the selected node changes → reload everything. */
  nodeKey?: string;
  className?: string;
  onViewAlert?: (alert: ViewAlertRequest) => void;
  /** Slotted in the centre of the toolbar — the board-view toggle. */
  headerSlot?: ReactNode;
  /** Full screen: drop the toolbar entirely (the board then owns the whole screen) and move
   *  the canvas's top-right controls down, clear of the page's exit-full-screen button. */
  hideToolbar?: boolean;
  onOpenRecordings?: (cameraId: string) => void;
  /** Fired when the placement editor opens or closes, so the toggle can lock while pins are
   *  unsaved — flipping the view would unmount this page and discard the draft. */
  onEditingChange?: (editing: boolean) => void;
}

export default function CameraGeoMapPage({
  api,
  alertsApi,
  PlayerComponent,
  canEdit = false,
  requireNode = false,
  nodeKey,
  className = 'p-3 sm:p-4',
  onViewAlert,
  headerSlot,
  hideToolbar = false,
  onOpenRecordings,
  onEditingChange,
}: CameraGeoMapPageProps) {
  const blocked = requireNode && !nodeKey;

  const [config, setConfig] = useState<GeoMapConfig | null>(null);
  // Map · Satellite · Terrain — this browser's choice, drawn over the site's configured map.
  const basemap = useBasemap(config);
  const [saved, setSaved] = useState<GeoPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Edit mode. `draft` is the working placement set; null means view mode.
  const [draft, setDraft] = useState<GeoPlacement[] | null>(null);
  const [armedCameraId, setArmedCameraId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  /**
   * Cameras fanned out around their shared spot. Active while the map is zoomed in at least
   * as far as `from`; zooming back out or clicking the map closes it.
   */
  const [spread, setSpread] = useState<{ ids: Set<string>; from: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);

  const mapRef = useRef<GeoTileMapHandle>(null);
  // The live view, tracked so "set as default" saves what is actually on screen. A ref, not
  // state: it changes on every settled pan and nothing renders from it.
  const viewRef = useRef<GeoView | null>(null);

  const editing = draft !== null;
  const placements = draft ?? saved;

  useEffect(() => {
    onEditingChange?.(editing);
  }, [editing, onEditingChange]);

  // ── Data ──────────────────────────────────────────────────────────────────

  const storageKey = `${STORAGE_KEY}${nodeKey ? `.${nodeKey}` : ''}`;

  useEffect(() => {
    if (blocked) {
      setSaved([]);
      setConfig(null);
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    setError('');
    setDraft(null);
    setSelectedId(null);
    Promise.all([api.config(), api.placements()])
      .then(([cfg, rows]) => {
        if (!mounted) return;
        setConfig(cfg);
        setSaved(rows);
      })
      .catch((e) => mounted && setError((e as Error).message || 'Could not load the map.'))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [api, nodeKey, blocked]);

  /**
   * Where the map opens: this browser's last position, else the deployment default.
   *
   * Resolved ONCE, when the config first lands — `GeoTileMap` takes it as a starting position
   * and never re-reads it, so an operator who has panned away is not yanked back.
   */
  const initialView = useMemo<GeoView | null>(() => {
    if (!config) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const v = JSON.parse(raw) as GeoView;
        if (Number.isFinite(v?.lat) && Number.isFinite(v?.lng) && Number.isFinite(v?.zoom)) return v;
      }
    } catch {
      /* private mode, or a value from an older shape — the deployment default is the answer */
    }
    return { lat: config.center_lat, lng: config.center_lng, zoom: config.zoom };
    // Only the FIRST config for this node decides where we open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!config, nodeKey]);

  const handleViewChange = useCallback(
    (v: GeoView) => {
      viewRef.current = v;
      setSpread((cur) => (cur && v.zoom < cur.from - 0.01 ? null : cur));
      try {
        localStorage.setItem(storageKey, JSON.stringify(v));
      } catch {
        /* private mode — the deployment default is still there on the next visit */
      }
    },
    [storageKey],
  );

  // ── Realtime, scoped to the cameras on the map ────────────────────────────

  const mapCameraIds = useMemo(
    () => new Set(placements.map((p) => p.camera_id)),
    // The id LIST, not the array identity — dragging a pin must not re-scope the alert panel
    // and remount every live tile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placements.map((p) => p.camera_id).sort().join(',')],
  );

  const board = useAlertBoard({ api: alertsApi, blocked, nodeKey, cameraFilter: mapCameraIds });
  const {
    cameras,
    cards,
    liveTiles,
    primaryCameraId,
    heldCameraIds,
    warmupCameraId,
    nameById,
    liveFor,
    recordingFor,
    colorStateFor,
    counterFor,
    latestAlertFor,
  } = board;

  /**
   * The same alert-type filter the honeycomb and the GIS plan carry, scoped to the cameras
   * actually placed on the map. DIMS rather than hides — a map an operator has learned the
   * shape of must not lose pins under a question.
   */
  const placedCameraIds = useMemo(() => placements.map((p) => p.camera_id), [placements]);
  const alertTypes = useAlertTypeFilter({ recent: board.recent, cards, cameraIds: placedCameraIds });

  /**
   * What the PANEL shows, under the alert-type filter.
   *
   * The map dims and the panel filters, which sounds inconsistent and is not: a dimmed pin
   * still tells the operator that camera exists and where it is, while a card for an alert
   * they have filtered out tells them nothing and pushes the ones they asked for off screen.
   *
   * Filtered per ALERT, so a camera carrying two kinds contributes only the matching one.
   * This narrows the page the server already returned; "Load more" still pages.
   */
  const visibleCards = useMemo(() => cards.filter(alertTypes.matches), [cards, alertTypes]);
  const visibleTiles = useMemo(
    () => liveTiles.filter((t) => alertTypes.matches(t.alert)),
    [liveTiles, alertTypes],
  );


  const cameraById = useMemo(() => {
    const m: Record<string, HexCamera> = {};
    cameras.forEach((c) => (m[c.id] = c));
    return m;
  }, [cameras]);

  const placementById = useMemo(() => {
    const m: Record<string, GeoPlacement> = {};
    placements.forEach((p) => (m[p.camera_id] = p));
    return m;
  }, [placements]);

  const nameOf = (cameraId: string) =>
    cameraById[cameraId]?.name || placementById[cameraId]?.camera_name || cameraId;

  /**
   * A camera missing from the alert roster still gets a pin — dropping it would make the map
   * quietly wrong. It falls back to the status denormalized onto the placement, read the way
   * the rest of the system reads connectivity: `streaming`/`recording` mean up, and
   * `connecting`/`error` do NOT (a dead camera loops through them forever).
   */
  const camFor = (cameraId: string): HexCamera => {
    const known = cameraById[cameraId];
    if (known) return known;
    const status = placementById[cameraId]?.camera_status;
    const up = status === 'streaming' || status === 'recording';
    return {
      id: cameraId,
      name: nameOf(cameraId),
      live: up,
      recording: up,
      online: up,
      groupId: null,
      groupName: null,
      siteId: '',
      siteName: null,
    };
  };

  // ── Placement editing ─────────────────────────────────────────────────────

  const beginEdit = useCallback(() => {
    setDraft(saved.map((p) => ({ ...p })));
    setSelectedId(null);
    setArmedCameraId(null);
    setError('');
  }, [saved]);

  const cancelEdit = useCallback(() => {
    setDraft(null);
    setArmedCameraId(null);
    setSelectedId(null);
    setError('');
  }, []);

  /** Click-to-place: the armed camera lands where the operator clicked the map. */
  const handleMapClick = useCallback(
    (lat: number, lng: number) => {
      setSpread(null);
      if (!editing || !armedCameraId) return;
      const cameraId = armedCameraId;
      setDraft((prev) => {
        const rest = (prev ?? []).filter((p) => p.camera_id !== cameraId);
        const previous = (prev ?? []).find((p) => p.camera_id === cameraId);
        return [
          ...rest,
          {
            camera_id: cameraId,
            lat,
            lng,
            // Re-placing a camera keeps the bearing it was surveyed with — the install did
            // not turn round just because the pin moved.
            rotation: previous?.rotation ?? null,
            fov_deg: previous?.fov_deg ?? null,
            camera_name: cameraById[cameraId]?.name,
            camera_status: previous?.camera_status,
          },
        ];
      });
      setSelectedId(cameraId);
      setArmedCameraId(null);
    },
    [editing, armedCameraId, cameraById],
  );

  /**
   * Drag a pin.
   *
   * The pointer position is turned back into a coordinate by the map, through the handle's
   * own container box — the marker layer is translated during a pan, so its own offsets
   * cannot be trusted mid-drag.
   */
  const dragRef = useRef<{ cameraId: string; moved: boolean; startX: number; startY: number } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const onMarkerPointerDown = useCallback(
    (cameraId: string) => (e: React.PointerEvent) => {
      if (!editing) return;
      // Without this the press also reaches the map and starts a pan under the pin.
      e.stopPropagation();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragRef.current = { cameraId, moved: false, startX: e.clientX, startY: e.clientY };
      setDraggingId(cameraId);
    },
    [editing],
  );

  useEffect(() => {
    if (!draggingId) return;
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      // Sub-slop jitter is a click (which selects the pin), not a drag.
      if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) <= 4) return;
      d.moved = true;
      const ll = mapRef.current?.clientToLatLng(e.clientX, e.clientY);
      if (!ll) return;
      setDraft((prev) =>
        prev ? prev.map((p) => (p.camera_id === d.cameraId ? { ...p, lat: ll.lat, lng: ll.lng } : p)) : prev,
      );
    };
    const onUp = () => {
      const d = dragRef.current;
      if (d && !d.moved) setSelectedId((cur) => (cur === d.cameraId ? null : d.cameraId));
      dragRef.current = null;
      setDraggingId(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [draggingId]);

  const removePlacement = useCallback((cameraId: string) => {
    setDraft((prev) => (prev ? prev.filter((p) => p.camera_id !== cameraId) : prev));
    setSelectedId((cur) => (cur === cameraId ? null : cur));
  }, []);

  const setRotation = useCallback((cameraId: string, rotation: number) => {
    setDraft((prev) => (prev ? prev.map((p) => (p.camera_id === cameraId ? { ...p, rotation } : p)) : prev));
  }, []);

  const setFov = useCallback((cameraId: string, fov: number) => {
    setDraft((prev) => (prev ? prev.map((p) => (p.camera_id === cameraId ? { ...p, fov_deg: fov } : p)) : prev));
  }, []);

  /**
   * Save the draft as a DELTA.
   *
   * `cleared` is what was on the map before and is not on it now — the node writes
   * coordinates onto camera rows, so sending only the survivors would leave the removed
   * cameras pinned forever.
   */
  async function savePlacements() {
    if (!draft) return;
    setSaving(true);
    setError('');
    try {
      const present = new Set(draft.map((p) => p.camera_id));
      const cleared = saved.filter((p) => !present.has(p.camera_id)).map((p) => p.camera_id);
      const rows = await api.savePlacements({
        placed: draft.map((p) => ({
          camera_id: p.camera_id,
          lat: p.lat,
          lng: p.lng,
          rotation: p.rotation ?? null,
          fov_deg: p.fov_deg ?? null,
        })),
        cleared,
      });
      setSaved(rows);
      setDraft(null);
      setArmedCameraId(null);
    } catch (e) {
      setError((e as Error).message || 'Could not save the camera positions.');
    } finally {
      setSaving(false);
    }
  }

  /** Save what is on screen as the view every operator opens on. */
  async function saveDefaultView() {
    const v = mapRef.current?.current() || viewRef.current;
    if (!v) return;
    setSavingDefault(true);
    setError('');
    setNotice('');
    try {
      setConfig(await api.saveConfig({ center_lat: v.lat, center_lng: v.lng, zoom: Math.round(v.zoom) }));
      setNotice('This is now the default map view for everyone on this site.');
    } catch (e) {
      setError((e as Error).message || 'Could not save the default view.');
    } finally {
      setSavingDefault(false);
    }
  }

  // Notices are confirmations, not state — they clear themselves rather than sitting in the
  // toolbar until the next navigation.
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  // ── Overlays ──────────────────────────────────────────────────────────────

  const selectedPlacement = selectedId ? placementById[selectedId] : undefined;
  const hoveredPlacement = hoveredId ? placementById[hoveredId] : undefined;

  // The pin is drawn at a constant screen size, so its half-width never changes.
  const markerHalfW = MARKER_SIZE / 2;

  /** The surveyed detail, for the hover card. */
  const hoverDetails = useMemo(() => {
    if (!hoveredPlacement) return undefined;
    const p = hoveredPlacement;
    return [
      {
        label: 'Facing',
        value: p.rotation == null ? '—' : `${Math.round(p.rotation)}° ${bearingLabel(p.rotation)}`,
      },
      { label: 'Field of view', value: `${Math.round(p.fov_deg ?? DEFAULT_FOV)}°` },
      { label: 'Coordinates', value: `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}` },
    ];
  }, [hoveredPlacement]);

  const alertSiblings = useMemo<ViewAlertTarget[]>(
    // `visibleCards`, not `cards`: this is what ← / → step through, and it is documented as
    // what the panel is SHOWING. Paging through alerts the operator has just filtered out
    // would be the arrow keys disagreeing with the list they were pressed in.
    () => visibleCards.map((c) => viewAlertTarget(c, nameById)),
    [visibleCards, nameById],
  );

  const handleViewAlert = useCallback(
    (alert: HexAlert) =>
      onViewAlert?.({
        ...viewAlertTarget(alert, nameById),
        siblings: alertSiblings,
        index: Math.max(0, alertSiblings.findIndex((s) => !!alert.id && s.id === alert.id)),
      }),
    [onViewAlert, nameById, alertSiblings],
  );

  /**
   * A group's circle was clicked. Cameras spread over an area: zoom to frame them, which
   * splits the group. Cameras on one spot: fan them out around it — straight away when the map
   * is already close in, otherwise once the zoom to that spot lands.
   */
  const openGroup = useCallback((members: Array<{ id: string; lat: number; lng: number }>, zoom: number) => {
    const oneSpot = groupSpanMetres(members) < SPREAD_SPAN_M;
    if (zoom >= SPREAD_ZOOM || oneSpot) {
      setSpread({ ids: new Set(members.map((m) => m.id)), from: Math.min(zoom, SPREAD_ZOOM) });
    }
    if (zoom < SPREAD_ZOOM) mapRef.current?.fitTo(members);
  }, []);

  /**
   * The pin layer. Memoized on what it actually draws, because GeoTileMap re-runs it on
   * every projection change — an inline closure would rebuild every marker on every zoom
   * regardless of whether anything about them moved.
   *
   * Outside edit mode, pins that would overlap at this zoom are drawn as one group circle
   * (MapCluster) coloured by the worst alert among them; zooming in splits them. The selected
   * camera always stays a pin of its own, so its preview card never points at a group.
   */
  const renderPins = useCallback(
    (project: (lat: number, lng: number) => { x: number; y: number }, zoom: number) => {
      const marker = (p: GeoPlacement, left: number, top: number) => {
        const cam = camFor(p.camera_id);
        return (
          <MapMarker
            key={p.camera_id}
            name={cam.name}
            dimmed={!editing && alertTypes.isDimmed(p.camera_id)}
            colorState={colorStateFor(cam)}
            counter={counterFor(cam)}
            live={liveFor(cam)}
            recording={recordingFor(cam)}
            left={left}
            top={top}
            rotation={p.rotation ?? 0}
            fov={p.fov_deg ?? DEFAULT_FOV}
            selected={selectedId === p.camera_id}
            editable={editing}
            dragging={draggingId === p.camera_id}
            onPointerDown={onMarkerPointerDown(p.camera_id)}
            onClick={() => {
              if (editing) return; // pointerup already resolved select-vs-drag
              setSelectedId((cur) => (cur === p.camera_id ? null : p.camera_id));
            }}
            onRemove={() => removePlacement(p.camera_id)}
            onHoverStart={() => setHoveredId(p.camera_id)}
            onHoverEnd={() => setHoveredId(null)}
          />
        );
      };

      const points: PinPoint<GeoPlacement>[] = placements.map((p) => {
        const pt = project(p.lat, p.lng);
        return { id: p.camera_id, x: pt.x, y: pt.y, lat: p.lat, lng: p.lng, item: p };
      });
      // Editing moves, turns and removes pins one at a time — every pin stays its own.
      if (editing) return points.map((pt) => marker(pt.item, pt.x, pt.y));

      const fanned = spread && zoom >= spread.from - 0.01 ? spread.ids : null;
      const out: ReactNode[] = [];
      const loose: PinPoint<GeoPlacement>[] = [];
      const fan: PinPoint<GeoPlacement>[] = [];
      for (const pt of points) {
        if (fanned?.has(pt.id)) fan.push(pt);
        else if (pt.id === selectedId) out.push(marker(pt.item, pt.x, pt.y));
        else loose.push(pt);
      }

      for (const g of clusterPins(loose, CLUSTER_RADIUS)) {
        if (g.members.length === 1) {
          const m = g.members[0];
          out.push(marker(m.item, m.x, m.y));
          continue;
        }
        // Colour and count from the cameras the alert-type filter leaves lit, so a group of
        // filtered-out cameras does not glow red for an alert the operator set aside.
        const lit = g.members.filter((m) => !alertTypes.isDimmed(m.id));
        const states: Partial<Record<HexColorState, number>> = {};
        let alerts = 0;
        for (const m of lit.length ? lit : g.members) {
          const cam = camFor(m.id);
          const st = colorStateFor(cam);
          states[st] = (states[st] ?? 0) + 1;
          alerts += counterFor(cam)?.total ?? 0;
        }
        out.push(
          <MapCluster
            key={`group:${g.key}`}
            left={g.x}
            top={g.y}
            count={g.members.length}
            states={states}
            alerts={alerts}
            dimmed={lit.length === 0}
            onClick={() => openGroup(g.members, zoom)}
          />,
        );
      }

      // Cameras on one spot, fanned out on a ring around it with a leg back to the spot.
      if (fan.length) {
        const cx = fan.reduce((t, m) => t + m.x, 0) / fan.length;
        const cy = fan.reduce((t, m) => t + m.y, 0) / fan.length;
        const r = Math.max(48, (fan.length * (MARKER_SIZE + 10)) / (2 * Math.PI));
        const box = r + MARKER_SIZE;
        const at = fan.map((m, i) => {
          const a = -Math.PI / 2 + (i * 2 * Math.PI) / fan.length;
          return { m, x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
        });
        out.push(
          <svg
            key="fan-legs"
            aria-hidden
            className="pointer-events-none absolute"
            style={{ left: cx - box, top: cy - box, width: box * 2, height: box * 2, overflow: 'visible' }}
          >
            {at.map(({ m, x, y }) => (
              <line key={m.id} x1={box} y1={box} x2={x - cx + box} y2={y - cy + box} stroke="#0f172a" strokeOpacity={0.45} strokeWidth={1.5} />
            ))}
          </svg>,
          <button
            key="fan-hub"
            type="button"
            title="Close"
            aria-label="Close the fanned-out cameras"
            onClick={(e) => {
              e.stopPropagation();
              setSpread(null);
            }}
            className="absolute h-3 w-3 rounded-full border-2 border-white bg-slate-900 shadow"
            style={{ left: cx, top: cy, transform: 'translate(-50%, -50%) scale(calc(1 / var(--map-k, 1)))' }}
          />,
        );
        at.forEach(({ m, x, y }) => out.push(marker(m.item, x, y)));
      }
      return out;
    },
    /**
     * `alertTypes.isDimmed` BELONGS HERE, and its absence was the bug.
     *
     * This layer is memoized on what it draws, because GeoTileMap re-runs it on every projection
     * change. The list left the alert-type filter out — so choosing an alert type recomputed the
     * dimming, and the map went on drawing the pins it had built before the question was asked:
     * the chips said "3 cameras", the panel filtered, and every pin on the map stayed lit. The
     * GIS plan renders its markers inline and never had the problem, which is why the two boards
     * disagreed.
     *
     * It is a `useCallback` keyed on the filter value and the alert names, so it changes exactly
     * when the pins must be redrawn and not once more.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placements, cameraById, board.counters, selectedId, editing, draggingId, onMarkerPointerDown,
      alertTypes.isDimmed, spread, openGroup, colorStateFor, counterFor],
  );

  const renderOverlay = useCallback(
    (project: (lat: number, lng: number) => { x: number; y: number }, size: { w: number; h: number }) => (
      <>
        {!editing && selectedPlacement && (() => {
          const cam = camFor(selectedPlacement.camera_id);
          const pt = project(selectedPlacement.lat, selectedPlacement.lng);
          const latest = latestAlertFor(cam.id);
          return (
            <ClickPreviewCard
              cameraId={cam.id}
              name={cam.name}
              caption={latest?.alertName || latest?.eventType || 'live'}
              live={liveFor(cam)}
              recording={recordingFor(cam)}
              counter={counterFor(cam)}
              anchorX={pt.x}
              anchorY={pt.y}
              hexHalfW={markerHalfW}
              viewportW={size.w}
              viewportH={size.h}
              PlayerComponent={PlayerComponent}
              onOpenRecordings={onOpenRecordings}
              onClose={() => setSelectedId(null)}
            />
          );
        })()}
        {!editing && hoveredPlacement && hoveredId !== selectedId && (() => {
          const cam = camFor(hoveredPlacement.camera_id);
          const pt = project(hoveredPlacement.lat, hoveredPlacement.lng);
          return (
            <HexAlertPopover
              alert={latestAlertFor(cam.id)}
              name={cam.name}
              live={liveFor(cam)}
              recording={recordingFor(cam)}
              counter={counterFor(cam)}
              anchorX={pt.x}
              anchorY={pt.y}
              hexHalfW={markerHalfW}
              viewportW={size.w}
              viewportH={size.h}
              details={hoverDetails}
              note={hoveredPlacement.camera_location}
              /* Cloud alerts carry no directly-loadable snapshotUrl — see the resolver's
                 note in HexAlertPopover. */
              loadPreview={alertsApi.loadPreview}
            />
          );
        })()}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editing, selectedPlacement, hoveredPlacement, hoveredId, selectedId, board.counters, hoverDetails],
  );

  const placedElsewhere = useMemo(() => ({}), []);

  return (
    <div className={`flex h-full flex-col gap-3 ${className}`}>
      {/* Toolbar — the same three zones as the other two boards, so the bar cannot reflow
          when the operator flips the view: left = what is on screen, centre = the view
          toggle, right = this board's actions. */}
      {!hideToolbar && (
      <div className="flex flex-shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 sm:px-3 sm:py-2">
        <div className="flex min-w-0 flex-1 items-center">
          <ToolbarIdentity
            icon={GlobeAltIcon}
            kind="Map:"
            title={blocked ? 'no site selected' : 'Camera locations'}
            detail={
              blocked
                ? 'Alert board · no site selected'
                : editing
                  ? (
                      <>
                        <span className="font-semibold uppercase tracking-wide text-brand">Editing</span>
                        {' · pick a camera, then click the map · drag a pin to move it'}
                      </>
                    )
                  : `Alert board · ${placements.length} camera${placements.length === 1 ? '' : 's'} on the map · click a pin to preview, a group to zoom in`
            }
          />
        </div>

        {/* ─── Centre zone — which view you're on ─── */}
        <div className="flex flex-none items-center justify-center">{headerSlot}</div>

        {/* ─── Right zone — this board's actions ─── */}
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:gap-2">
          {editing ? (
            <>
              <Tooltip label={saving ? 'Saving…' : 'Save camera positions'} side="bottom">
                <button
                  type="button"
                  onClick={savePlacements}
                  disabled={saving}
                  aria-label="Save camera positions"
                  className="rounded-lg bg-brand p-1.5 text-white shadow-sm ring-1 ring-inset ring-white/10 transition-all hover:bg-brand/90 active:scale-[0.95] disabled:pointer-events-none disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </Tooltip>
              <Tooltip label="Discard changes" side="bottom">
                <button
                  type="button"
                  onClick={cancelEdit}
                  aria-label="Cancel"
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </Tooltip>
            </>
          ) : (
            canEdit && !blocked && (
              <>
                <Tooltip label="Place cameras on the map" side="bottom">
                  <button
                    type="button"
                    onClick={beginEdit}
                    aria-label="Mark cameras"
                    className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  >
                    <MapPinIcon className="h-4 w-4" />
                  </button>
                </Tooltip>
                {/* The whole point of the default view: an operator frames the site once and
                    every other operator — and every reload — opens there. */}
                <Tooltip
                  label={savingDefault ? 'Saving…' : 'Save this position and zoom as the default view'}
                  side="bottom"
                >
                  <button
                    type="button"
                    onClick={saveDefaultView}
                    disabled={savingDefault}
                    className="flex h-7 items-center gap-1 rounded-lg border border-border px-2 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                  >
                    <CheckIcon className="h-3.5 w-3.5" /> Set default view
                  </button>
                </Tooltip>
              </>
            )
          )}
        </div>
      </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-lg border border-brand/40 bg-brand/10 px-3 py-2 text-xs text-brand">{notice}</div>
      )}

      {/* Only while there is something to filter BY — a bar offering "All alerts" and nothing
          else cannot be used, and it costs a row of the map's height. */}
      {!blocked && !editing && alertTypes.options.length > 0 && (
        <HexFilterBar
          groups={[alertTypes.group]}
          matchCount={alertTypes.matchCount}
          totalCount={placedCameraIds.length}
          noun="pins"
          className="lg:hidden"
        />
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
        {/* Wide screens: the same filter as the left column (phones keep the bar above). */}
        {!blocked && !editing && alertTypes.options.length > 0 && (
          <HexFilterColumn
            groups={[alertTypes.group]}
            matchCount={alertTypes.matchCount}
            totalCount={placedCameraIds.length}
            noun="pins"
            className="hidden lg:block"
          />
        )}
        {/* Edit rail — cameras to place, plus the selected pin's facing. */}
        {editing && (
          <aside className="flex max-h-[40vh] min-h-0 flex-none flex-col gap-3 rounded-lg border border-border bg-card p-3 lg:max-h-none lg:w-[240px]">
            <CameraPalette
              cameras={cameras}
              placedHere={mapCameraIds}
              // Nothing is "placed elsewhere" here: the map is one surface, not a set of
              // layouts a camera can only belong to one of.
              placedElsewhere={placedElsewhere}
              armedId={armedCameraId}
              onArm={setArmedCameraId}
            />
            {selectedPlacement && (
              <div className="flex-none space-y-2 border-t border-border pt-2">
                <p className="truncate text-xs font-semibold text-foreground">
                  {nameOf(selectedPlacement.camera_id)}
                </p>
                <p className="text-[10px] tabular-nums text-muted-foreground">
                  {selectedPlacement.lat.toFixed(5)}, {selectedPlacement.lng.toFixed(5)}
                </p>
                <label className="block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Facing {Math.round(selectedPlacement.rotation ?? 0)}°{' '}
                  {bearingLabel(selectedPlacement.rotation ?? 0)}
                </label>
                <input
                  type="range"
                  min={0}
                  max={360}
                  value={selectedPlacement.rotation ?? 0}
                  onChange={(e) => setRotation(selectedPlacement.camera_id, Number(e.target.value))}
                  className="w-full accent-[color:var(--brand,#015ab5)]"
                />
                <label className="block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Field of view {Math.round(selectedPlacement.fov_deg ?? DEFAULT_FOV)}°
                </label>
                <input
                  type="range"
                  min={10}
                  max={360}
                  value={selectedPlacement.fov_deg ?? DEFAULT_FOV}
                  onChange={(e) => setFov(selectedPlacement.camera_id, Number(e.target.value))}
                  className="w-full accent-[color:var(--brand,#015ab5)]"
                />
                <button
                  type="button"
                  onClick={() => removePlacement(selectedPlacement.camera_id)}
                  className="w-full rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  Remove from map
                </button>
              </div>
            )}
          </aside>
        )}

        <div
          className={`relative overflow-hidden rounded-lg border border-border bg-card lg:h-auto lg:min-h-0 lg:flex-1 ${
            editing ? 'min-h-[320px] flex-1' : 'h-[45vh] min-h-[260px] flex-none'
          }`}
        >
          {blocked ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Select a site to see its cameras on the map.
            </div>
          ) : loading || !config || !initialView ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Loading map…
            </div>
          ) : (
            <>
              <GeoTileMap
                handleRef={mapRef}
                source={basemap.source}
                initialView={initialView}
                onViewChange={handleViewChange}
                onMapClick={handleMapClick}
                placing={editing && !!armedCameraId}
                pins={renderPins}
                overlay={renderOverlay}
                trackOverlay={!editing && (!!selectedId || !!hoveredId)}
              />

              {/* Find a place instead of dragging the world into position by hand. Top-LEFT,
                  opposite the zoom cluster, so the dropdown never opens under it. */}
              <div className="absolute left-3 top-3 z-[800]">
                <MapSearchBox
                  geocoderUrl={config.geocoder_url}
                  editing={editing}
                  onGo={({ lat, lng, zoom }) =>
                    mapRef.current?.setView({
                      lat,
                      lng,
                      // A query that named no zoom keeps the operator's own.
                      zoom: zoom ?? mapRef.current?.current()?.zoom ?? config.zoom,
                    })
                  }
                  // A city or a district comes back with a bounding box, and framing it says
                  // "here is the area" where dropping to street level would say "here is a
                  // rooftop" — and hide every camera the operator was about to place near it.
                  onFit={([south, west, north, east]) =>
                    mapRef.current?.fitTo([
                      { lat: south, lng: west },
                      { lat: north, lng: east },
                    ])
                  }
                />
              </div>

              {/* Zoom + framing. `Fit` frames the cameras, not the world — the fastest way
                  back from a stray double-click that left the operator over an ocean. */}
              <div className={`absolute right-3 ${hideToolbar ? 'top-14' : 'top-3'} z-[700] flex items-center gap-1 rounded-md border border-border bg-card/90 p-0.5 shadow-card backdrop-blur-sm`}>
                <button
                  type="button"
                  title="Zoom out"
                  onClick={() => mapRef.current?.zoomBy(-1)}
                  className="flex h-6 w-6 items-center justify-center rounded text-foreground hover:bg-muted"
                >
                  <MinusIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  title="Zoom in"
                  onClick={() => mapRef.current?.zoomBy(1)}
                  className="flex h-6 w-6 items-center justify-center rounded text-foreground hover:bg-muted"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                </button>
                <span className="h-4 w-px bg-border" aria-hidden />
                <button
                  type="button"
                  title="Frame every camera on the map"
                  disabled={!placements.length}
                  onClick={() => mapRef.current?.fitTo(placements)}
                  className="flex h-6 items-center gap-1 rounded px-1.5 text-xs text-foreground hover:bg-muted disabled:opacity-40"
                >
                  <ArrowsPointingOutIcon className="h-3.5 w-3.5" /> Fit
                </button>
                {config.is_configured && (
                  <>
                    <span className="h-4 w-px bg-border" aria-hidden />
                    <button
                      type="button"
                      title="Back to the saved default view"
                      onClick={() =>
                        mapRef.current?.setView({
                          lat: config.center_lat,
                          lng: config.center_lng,
                          zoom: config.zoom,
                        })
                      }
                      className="flex h-6 items-center rounded px-1.5 text-xs text-foreground hover:bg-muted"
                    >
                      Default
                    </button>
                  </>
                )}
              </div>

              {/* Nothing placed yet — say how to place something rather than leaving an
                  operator to guess that the empty map is the feature working. */}
              {!placements.length && !editing && (
                <div className="pointer-events-none absolute inset-x-0 bottom-10 z-[700] flex justify-center">
                  <p className="rounded-md border border-border bg-card/95 px-3 py-1.5 text-[11px] text-muted-foreground shadow-card">
                    No cameras on the map yet
                    {canEdit ? ' — use the pin button to place them.' : '.'}
                  </p>
                </div>
              )}

              {/* Bottom-RIGHT, above the map's credits button — the legend owns the
                  bottom-left, and the top corners hold search and zoom. */}
              <BasemapSwitcher
                value={basemap.id}
                onChange={basemap.setId}
                className="absolute bottom-11 right-3 z-[700]"
              />

              <BoardLegend marker="pin" />
            </>
          )}
        </div>

        {/* Hidden transport warmer — see the note in useAlertBoard. */}
        {warmupCameraId && (
          <div aria-hidden className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0">
            <PlayerComponent cameraId={warmupCameraId} label="warmup" className="h-px w-px" />
          </div>
        )}

        {/* The alert panel stands down while marking, exactly as on the GIS board: placing
            pins is a survey task that wants every pixel of map. */}
        {!editing && (
          <aside className="flex min-h-0 flex-1 flex-col rounded-lg border border-border bg-card p-3 lg:w-[360px] lg:flex-none lg:p-4">
            <AlertTileStack
              liveTiles={visibleTiles}
              persistentCameraId={primaryCameraId}
              heldCameraIds={heldCameraIds}
              cards={visibleCards}
              nameById={nameById}
              counters={board.counters}
              PlayerComponent={PlayerComponent}
              onSelect={(id) => setSelectedId(id)}
              onExpire={board.handleTileExpire}
              onContinue={board.handleTileContinue}
              onAcknowledge={alertsApi.acknowledgeAlert ? board.handleAcknowledge : undefined}
              onResolve={alertsApi.resolveAlert ? board.handleResolve : undefined}
              onViewAlert={onViewAlert ? handleViewAlert : undefined}
              onOpenRecordings={onOpenRecordings}
              loadPreview={alertsApi.loadPreview}
              hasMore={board.hasMore}
              loadingMore={board.loadingCards}
              onLoadMore={board.loadMoreCards}
            />
          </aside>
        )}
      </div>
    </div>
  );
}
